import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Card, CardContent, LinearProgress, Stack, Typography } from "@mui/material";
import { useTranslation } from "i18n-fe";
import { filterVisibleClasses } from "score-common";
import { TableSkeleton, useActiveShow } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import RingStateSection from "../components/RingStateSection";
import ScribePresenceSection from "../components/ScribePresenceSection";
import useRingStates from "../hooks/useRingStates";

// #823 — the dashboard was a one-shot fetch (only re-ran when activeShowId
// changed), so anyone who left it open during a live show read stale counts
// forever. Poll and refresh-on-focus, same visibility-aware shape as
// announcer-fe's useClassResults.js / common-fe's useRideStatuses.js: pause
// while the tab is hidden, refresh immediately the moment it's visible again
// so nothing is stale on return. 20s (vs. announcer's 5-15s) because this
// aggregate re-walks every class in the show (2 calls each), not one class's
// results.
const DASHBOARD_POLL_MS = 20000;

// #511: replace the placeholder with a real read-only counts/completion
// dashboard for the selected show. Shape (useScoreApi/unwrap, TableSkeleton
// loading region) originally copied from ClassManager.jsx / PeopleManager.jsx;
// #823 swapped the one-shot fetch those pages use for the visibility-aware
// poll below, since a dashboard (unlike a CRUD manager page) needs to stay
// current while left open.
//
// #2699 split this single aggregate in two, per the caching constraint
// Jaybill left on the card: classes (and their order) rarely change during a
// show, so they're safe to cache and paint from cache immediately; anything
// derived from RIDE data — including counts — is in constant flux and must
// never be served stale. So:
//   classes = listClasses(showId)              -- cached across mounts, see
//                                                  classesCache below.
//   rides   = listRides(undefined, showId)     -- #2735: ONE show-scoped
//                                                  call, bucketed by classId
//                                                  client-side, replacing a
//                                                  per-class listRides(c.id).
//   results = getShowResultsSummary(showId)    -- #2735: ONE show-scoped
//                                                  call, replacing a
//                                                  per-class getClassResults(c.id).
//   classCount   = classes.length               -- from the cached list.
//   rideCount    = sum of rides.length           -- NEVER cached, polled.
//   riderCount   = distinct ride.riderId across all classes  -- NEVER cached.
//   horseCount   = distinct ride.horseId across all classes  -- NEVER cached.
//   finalCount   = sum of results where status === "FINAL"   -- NEVER cached.
// (results.js:buildRideResult returns exactly one result per ride, so
// finalCount/rideCount is directly comparable.)
//
// Each half also gets its own loading flag so a section whose data has
// already resolved paints immediately instead of waiting on the slower one
// (classes is one flat GET; the ride/result loop below is an N+1 over every
// class in the show and is usually the slower of the two).

// Module-level, in-memory, per-session cache of a show's class list, keyed
// by showId. This is what makes a repeat MOUNT of ShowDashboard (navigate
// away and back, no full reload) paint the classes stat instantly with NO
// new network request — see getClassesForShow.
//
// Judge notes on a654df27 (#2699), both addressed here:
//
// F2 — score-api's listClasses response is NOT a bare class row: it
// enriches every item with ride-derived fields (`scheduledTime` off the
// show's rides, `fvdPushState` off ride/push state — see
// apps/score-api/src/handlers/class.js:323). Caching the raw response would
// cache those ride-derived fields forever, violating the card comment even
// though the dashboard itself only ever reads classCount/`.id` today.
// Fixed by projecting to the one field anything here actually uses before
// it goes in the cache — see fetchAndCacheClasses below. If a future
// consumer of this cache (the sister classes-page card in this same
// workstream is the obvious one) needs scheduledTime/fvdPushState, it must
// NOT read them off this cache; those are ride-derived and go stale exactly
// like rideCount/riderCount/etc.
//
// F3 — caching classes must not silently freeze the QUERY SCOPE of the
// ride-derived totals: a class added or removed mid-show has to be picked
// up eventually, or rideCount/riderCount/horseCount/finalCount undercount
// or error (a removed class's listRides call fails) for the rest of the
// browser session with no recovery short of a full reload. Decision:
// stale-while-revalidate keyed off the SAME hasLoadedForShowRef the
// ride-agg skeleton already uses — a MOUNT (hasLoadedForShowRef.current
// false) reads cache-only via getClassesForShow so AC2's "no new request
// on a repeat mount" holds; every POLL TICK after that first load
// (hasLoadedForShowRef.current true) revalidates via
// revalidateClassesForShow, so an added/removed class is reflected, and the
// stale-class window this trades in is bounded by DASHBOARD_POLL_MS (20s)
// rather than "until next full reload". Rejected alternative: never
// revalidate at all (today's a654df27 behaviour) — simplest, but leaves a
// removed class's listRides call failing on every tick forever with no way
// back except a reload, which is worse than the "very rarely" staleness
// window the card comment accepts for classes themselves.
const classesCache = new Map();
// In-flight de-dupe (F6c): two components racing a cache miss for the same
// show (e.g. the dashboard mounting while another tab/consumer is also
// resolving classesCache) share one request instead of firing two.
const classesInFlight = new Map();

async function fetchAndCacheClasses(scoreApi, showId) {
  const list = unwrap(await scoreApi.classes.listClasses(showId));
  // F2 — project to the one field anything reading this cache uses today.
  // Deliberately drops score-api's ride-derived scheduledTime/fvdPushState
  // so this cache can never be a source of stale ride data.
  // #1117 -- a hidden class (hand-hidden, or an emptied RO/ROO split) is not
  // counted on the dashboard. Presentation only, the class stays in the data.
  const projected = filterVisibleClasses(list).map((c) => ({ id: c.id }));
  classesCache.set(showId, projected);
  return projected;
}

function revalidateClassesForShow(scoreApi, showId) {
  if (classesInFlight.has(showId)) return classesInFlight.get(showId);
  const promise = fetchAndCacheClasses(scoreApi, showId).finally(() => {
    classesInFlight.delete(showId);
  });
  classesInFlight.set(showId, promise);
  return promise;
}

function getClassesForShow(scoreApi, showId) {
  if (classesCache.has(showId)) return Promise.resolve(classesCache.get(showId));
  return revalidateClassesForShow(scoreApi, showId);
}

// #2735 — collapses the N+1 this comment used to describe (a per-class
// listRides(c.id) + getClassResults(c.id), 2N score-api requests for an
// N-class show, doubled again on every poll tick). Now ONE show-scoped
// listRides(undefined, showId) call (the #340 shape already used by
// useRingStates and useClassesNeedingPlacings.js:60 on this same page) plus
// ONE show-scoped getShowResultsSummary(showId) call (card #2735's new
// score-api route — see its handler comment for why this is a sibling of
// getClassResultsSummary rather than a mode on it), regardless of how many
// classes the show has. Still never cached — rides/results are "in constant
// flux during a show" (#2699 comment), so every call site here re-reads them
// fresh, on mount and on every poll tick alike.
//
// rideCount/riderCount/horseCount are bucketed off the show-wide rides by
// classId (mirrors useClassesNeedingPlacings' own ridesByClassId bucketing),
// restricted to the class list passed in so a class removed from the cached
// list mid-show doesn't silently inflate the total. finalCount is summed off
// the show-results-summary rows for those same classIds, defaulting a class
// missing from that response (no Rides in the show yet) to 0 — the same
// number a per-class getClassResults(classId) call would have summed to.
//
// #2735 judge finding (equivalence defect) — listRides(undefined, showId)
// queries the ridesByShow GSI, which structurally can't return a Ride with
// no `showId` at all. createManualRide (#386, the emergency manual-add-ride
// path) writes one of those when it can't resolve a showId at create time
// ("a ride with no showId is still scorable" -- its own comment). The OLD
// per-class listRides(c.id) found such a ride via the ridesByClass GSI
// (classId is always present), so it has to be found here too. Fix: a THIRD
// call, listRides(undefined, undefined, undefined, undefined, true)
// (needsReview mode -- #388's org-wide review queue, already an existing
// listRides mode, not a new endpoint), filtered to rides missing showId
// whose classId is one of THIS show's classes (the `classes` param already
// scopes that -- no extra showId lookup needed, unlike the score-api side of
// this same fix). This is a fixed, bounded, ORG-WIDE query (the review queue
// is a handful of rows, not one per class), so AC1's "does not grow with
// class count" still holds with three calls instead of two. A ride that
// already has a showId is already covered by the first call and is filtered
// out here to avoid double-counting.
//
// #3107 -- `classes` may be the class list OR a promise for it. None of the
// three requests above depends on the class list (it is only used afterwards,
// to restrict and bucket what came back), so the caller can start this while
// its own GET /class is still in flight instead of awaiting /class first. That
// await was one of the two serial API legs ahead of the dashboard's
// first-interactive (979's "+150 ms per call moved first-interactive +265 ms"):
// /class, THEN /ride + /show-results-summary. With the promise form both legs
// go out in the same tick and the page waits for the slower, not the sum.
export async function loadRideAggregate(scoreApi, showId, classesOrPromise) {
  // The three calls must be plain (unawaited) promises going into Promise.all
  // -- an `await` on each element here would serialize them (each one only
  // starting once the previous settles), which is exactly the per-request
  // latency this card collapses the N+1 fan-out to avoid. `.then(unwrap)`
  // keeps them concurrent, the same shape useRingStates.js already uses.
  const [[showRides, resultsSummary, reviewRides], classes] = await Promise.all([
    Promise.all([
      scoreApi.rides.listRides(undefined, showId).then(unwrap),
      scoreApi.results.getShowResultsSummary(showId).then(unwrap),
      scoreApi.rides.listRides(undefined, undefined, undefined, undefined, true).then(unwrap),
    ]),
    classesOrPromise,
  ]);

  const classIdSet = new Set(classes.map((c) => c.id));
  const showlessRides = (reviewRides || []).filter(
    (ride) => !ride.showId && classIdSet.has(ride.classId)
  );

  const ridesByClassId = new Map();
  for (const ride of [...showRides, ...showlessRides]) {
    if (!ride?.classId) continue;
    const bucket = ridesByClassId.get(ride.classId);
    if (bucket) bucket.push(ride);
    else ridesByClassId.set(ride.classId, [ride]);
  }
  const finalCountByClassId = new Map(
    (resultsSummary || []).map((row) => [row.classId, row.finalCount])
  );

  const riderIds = new Set();
  const horseIds = new Set();
  let rideCount = 0;
  let finalCount = 0;

  for (const c of classes) {
    const rides = ridesByClassId.get(c.id) || [];
    rideCount += rides.length;
    for (const ride of rides) {
      if (ride.riderId) riderIds.add(ride.riderId);
      if (ride.horseId) horseIds.add(ride.horseId);
    }
    finalCount += finalCountByClassId.get(c.id) || 0;
  }

  return {
    rideCount,
    riderCount: riderIds.size,
    horseCount: horseIds.size,
    finalCount,
  };
}

// #1061 — the on-page heading (NOT the sidebar nav label, NOT the browser
// tab title — those stay "Dashboard") shows the active show's name instead
// of the generic "Show dashboard". The show's year is appended after the
// name, but only when the name doesn't already contain one (a show is
// commonly named "Foo Classic 2026", and doubling the year there would be
// redundant/wrong) — checked with a real-year-shaped token match anywhere in
// the string, not just at the end, so "2026 Foo Classic" also counts.
const YEAR_TOKEN_RE = /\b(19|20)\d{2}\b/;

// activeShow.startDate is a bare YYYY-MM-DD (see Settings.jsx's
// formatShowDate) — split it rather than routing through `new Date(iso)`,
// which treats a date-only string as UTC midnight and can roll the year
// back a day off in far-eastern/western clocks.
function showYearFrom(startDate) {
  if (!startDate) return null;
  const [y] = startDate.split("-");
  const year = Number(y);
  return Number.isInteger(year) && year > 0 ? String(year) : null;
}

function dashboardHeading(t, activeShow) {
  const name = activeShow?.name;
  if (!name) return t("dashboard.title");
  const year = showYearFrom(activeShow?.startDate);
  if (!year || YEAR_TOKEN_RE.test(name)) return t("dashboard.heading", { name });
  return t("dashboard.headingWithYear", { name, year });
}

// #1060 — the org's uploaded logo (score-api's `GET /show` family already
// returns a presigned `logoUrl` off the stored `logoKey`, per #1058) renders
// right-aligned next to the #1061 heading, with the org name (the same
// `organization` field the report footer / Settings.jsx's LogoCard read)
// underneath it. Bounded size — large but not full-bleed — via maxHeight/
// maxWidth + objectFit: "contain", same convention as Settings.jsx's
// show-logo-preview. No logo: fall back to the org name alone (cleaner than
// an empty box); no org name either: render nothing rather than an artifact.
function ShowLogoBlock({ activeShow, t }) {
  const logoUrl = activeShow?.logoUrl;
  const organization = activeShow?.organization;

  if (!logoUrl && !organization) return null;

  return (
    <Stack alignItems="flex-end" spacing={0.5} data-testid="dashboard-logo-block">
      {logoUrl && (
        <Box
          component="img"
          src={logoUrl}
          alt={t("dashboard.logoAlt", { organization: organization || "" })}
          data-testid="dashboard-logo"
          sx={{
            maxHeight: { xs: 64, sm: 100 },
            maxWidth: { xs: 160, sm: 240 },
            objectFit: "contain",
          }}
        />
      )}
      {organization && (
        <Typography variant="body2" color="text.primary" data-testid="dashboard-org-name">
          {organization}
        </Typography>
      )}
    </Stack>
  );
}

function StatCard({ label, value }) {
  return (
    <Card variant="outlined" sx={{ minWidth: 140, flex: "1 1 140px" }}>
      <CardContent>
        <Typography variant="h4" color="text.primary">
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function ShowDashboard() {
  const { t } = useTranslation("scoreadmin");
  useDocumentTitle(t("title.dashboard"));
  const scoreApi = useScoreApi();
  const { activeShowId, activeShow, loading: loadingShows } = useActiveShow();

  // Single fetch for RingStateSection AND ScribePresenceSection (#2365): both
  // need the same per-ring class/ride/ring-state data, and calling
  // useRingStates a second time here duplicated every underlying request
  // (listClasses/listRides/listRingStates) on every dashboard mount -- real
  // extra load, and enough added concurrent traffic during mount to push the
  // #823 "pauses polling while hidden" test's timing past its margin. One
  // fetch, passed down as a prop to both.
  const ringState = useRingStates(activeShowId);

  const [error, setError] = useState(null);

  // Classes half (#2699): cache-first. Seeded synchronously from the
  // module-level classesCache when a prior mount already resolved this
  // show's classes, so a repeat mount paints classCount with NO loading
  // flash and NO new request (see getClassesForShow).
  const [classes, setClasses] = useState(() => classesCache.get(activeShowId) ?? null);
  // F6a — both this initial state and the activeShowId effect below key off
  // classesCache.has(), never a truthiness check on .get(), so an eventual
  // zero-class show (an empty but cached array) isn't treated as a miss by
  // one and a hit by the other.
  const [classesLoading, setClassesLoading] = useState(() => !classesCache.has(activeShowId));

  // Ride-derived half (#2699): NEVER cached, always polled fresh.
  const [rideAgg, setRideAgg] = useState(null);
  const [rideAggLoading, setRideAggLoading] = useState(true);

  const scoreApiRef = useRef(scoreApi);
  scoreApiRef.current = scoreApi;
  const showIdRef = useRef(activeShowId);
  showIdRef.current = activeShowId;

  // Only the FIRST load for a given show shows the ride-agg skeleton — a
  // background poll refresh swaps the numbers in place, it doesn't blank
  // the screen. The classes half doesn't need this: its own loading flag is
  // already cache-driven (false immediately on a cache hit).
  const hasLoadedForShowRef = useRef(false);
  useEffect(() => {
    hasLoadedForShowRef.current = false;
    if (classesCache.has(activeShowId)) {
      setClasses(classesCache.get(activeShowId));
      setClassesLoading(false);
    } else {
      setClasses(null);
      setClassesLoading(activeShowId != null);
    }
    // F5 — reset the ride-derived half here too, or a show switch that
    // misses the classes cache leaves the PREVIOUS show's rides/riders/
    // horses/completion on screen, under the NEW show's heading, for the
    // whole classes round trip (rideAggLoading only flips back to true once
    // refresh() below reaches its own "first load for this show" check,
    // which is after the classes fetch).
    setRideAgg(null);
    setRideAggLoading(activeShowId != null);
  }, [activeShowId]);

  const refresh = useCallback(async () => {
    const showId = showIdRef.current;
    if (!showId) return;
    // F5 — hoisted above the classes fetch (was after it) so the ride-agg
    // skeleton is already showing before that await, not just before the
    // ride/result loop that follows it.
    if (!hasLoadedForShowRef.current) setRideAggLoading(true);
    try {
      // F3 — cache-only on this show's first load (mount), so a repeat
      // mount with a cache hit issues no request (AC2); revalidate on every
      // poll tick after that, so an added/removed class is picked up within
      // one poll interval instead of frozen for the session. See the
      // classesCache comment block above for the full decision.
      //
      // #3107 -- the ride aggregate no longer waits for the class list: start
      // it with the class PROMISE so /class, /ride and /show-results-summary
      // are all in flight at once (they used to be /class, then the other two).
      const classPromise = hasLoadedForShowRef.current
        ? revalidateClassesForShow(scoreApiRef.current, showId)
        : getClassesForShow(scoreApiRef.current, showId);
      const aggPromise = loadRideAggregate(scoreApiRef.current, showId, classPromise);
      // If /class rejects, aggPromise rejects with the same error but is never
      // awaited (the await below throws first); mark it handled so it does not
      // surface as an unhandledrejection on top of the real error.
      aggPromise.catch(() => {});
      const classList = await classPromise;
      setClasses(classList);
      setClassesLoading(false);

      const result = await aggPromise;
      setRideAgg(result);
      setError(null);
    } catch (err) {
      // F6b — a classes-fetch failure used to leave classesLoading stuck
      // true forever (only the finally below cleared rideAggLoading), so
      // the classes stat skeletoned forever after one failed request.
      setError(err);
      setClassesLoading(false);
    } finally {
      hasLoadedForShowRef.current = true;
      setRideAggLoading(false);
    }
  }, []);

  // (Re)load for the active show, then keep the ride-derived half current:
  // poll at DASHBOARD_POLL_MS, pausing while the tab is hidden and
  // refreshing immediately on return, so a scoreadmin who leaves this open
  // during a show sees live counts rather than whatever was true when the
  // tab loaded. The classes half rides along on every tick too, and per the
  // F3 decision above, a tick past the first load DOES revalidate the class
  // list against score-api rather than reusing the cache — the cost of that
  // extra request is what keeps an added/removed class from being frozen
  // out of the ride totals for the rest of the session. Only a first-ever
  // resolve for a show shows the ride-agg skeleton; the classes half never
  // re-skeletons on a revalidation tick, it swaps in place like the ride
  // totals do.
  useEffect(() => {
    if (!activeShowId) {
      setRideAgg(null);
      setRideAggLoading(false);
      return undefined;
    }

    if (document.visibilityState !== "hidden") refresh();

    let timer = null;
    const start = () => {
      if (document.visibilityState === "hidden") return;
      if (timer == null) timer = setInterval(refresh, DASHBOARD_POLL_MS);
    };
    const stop = () => {
      if (timer != null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") stop();
      else {
        refresh();
        start();
      }
    };
    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [activeShowId, refresh]);

  // MD3 loading convention (#514): the initial show-list fetch is a full
  // content region, so it renders a shaped skeleton (not a blank flash).
  if (loadingShows) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="h4" gutterBottom color="text.primary" data-testid="dashboard-heading">
            {dashboardHeading(t, activeShow)}
          </Typography>
          <ShowLogoBlock activeShow={activeShow} t={t} />
        </Stack>
        <TableSkeleton rows={1} columns={4} />
      </Box>
    );
  }

  const completionPct =
    rideAgg && rideAgg.rideCount > 0 ? (rideAgg.finalCount / rideAgg.rideCount) * 100 : 0;
  const classCount = classes ? classes.length : 0;

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={2}
      >
        <Typography variant="h4" gutterBottom color="text.primary" data-testid="dashboard-heading">
          {dashboardHeading(t, activeShow)}
        </Typography>
        <ShowLogoBlock activeShow={activeShow} t={t} />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || t("dashboard.loadError")}
        </Alert>
      )}

      {!activeShowId && <Typography color="text.secondary">{t("dashboard.noShows")}</Typography>}

      {/* #2699 — each section below carries its OWN loading flag and renders
          its own skeleton in place, rather than one big gate that makes
          every section wait on the slowest one (previously: nothing here
          rendered at all — not even the Ring/Scribe sections below, which
          already had their own independent fetch/skeleton — until the
          ride/result N+1 loop finished). A section whose data has already
          resolved paints immediately; a section still in flight shows a
          skeleton, never a bare empty state (AC3). */}
      {activeShowId && (
        <Stack spacing={3} data-testid="dashboard-stats">
          <Stack direction="row" flexWrap="wrap" gap={2}>
            <Box data-testid="dashboard-stat-classes" sx={{ flex: "1 1 140px", minWidth: 140 }}>
              {classesLoading ? (
                <TableSkeleton rows={1} columns={1} showHeader={false} />
              ) : (
                <StatCard label={t("dashboard.stats.classes")} value={classCount} />
              )}
            </Box>

            <Box
              data-testid="dashboard-stat-ride-group"
              sx={{ display: "flex", flexWrap: "wrap", gap: 2, flex: "3 1 420px" }}
            >
              {/* F1 — gate on !rideAgg too, not just rideAggLoading. refresh()'s
                  catch never sets rideAgg, only its finally clears
                  rideAggLoading, so a failed first load (network blip, a
                  401 mid silent-renew, a listRides 404 on a deleted class)
                  used to reach rideAgg.rideCount with rideAgg === null and
                  throw during render, blanking the whole page instead of
                  showing the loadError Alert above. */}
              {rideAggLoading || !rideAgg ? (
                <TableSkeleton rows={1} columns={3} showHeader={false} sx={{ flex: 1 }} />
              ) : (
                <>
                  <StatCard label={t("dashboard.stats.rides")} value={rideAgg.rideCount} />
                  <StatCard label={t("dashboard.stats.riders")} value={rideAgg.riderCount} />
                  <StatCard label={t("dashboard.stats.horses")} value={rideAgg.horseCount} />
                </>
              )}
            </Box>
          </Stack>

          <Box data-testid="dashboard-completion">
            {/* F1 — same null guard as the stat cards above. */}
            {rideAggLoading || !rideAgg ? (
              <Card variant="outlined">
                <CardContent>
                  <TableSkeleton rows={2} columns={1} showHeader={false} />
                </CardContent>
              </Card>
            ) : (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom color="text.primary">
                    {t("dashboard.completion.title")}
                  </Typography>
                  <Typography variant="body1" color="text.primary" gutterBottom>
                    {t("dashboard.completion.summary", {
                      final: rideAgg.finalCount,
                      total: rideAgg.rideCount,
                    })}
                  </Typography>
                  <LinearProgress variant="determinate" value={completionPct} />
                </CardContent>
              </Card>
            )}
          </Box>

          {/* #1936 (docs/score/ring-state.md) — the Ring section: every ring
              in the show, what's in it, where that came from, and one-click
              controls to fix it. This is the live-monitoring screen the
              card's "screen you open mid-class" framing points at. Renders
              unconditionally once a show is active — it manages its own
              independent fetch/skeleton via useRingStates and never waited
              on the stat-card aggregate above. */}
          <RingStateSection showId={activeShowId} ringState={ringState} />

          {/* #2365 (scribe-presence workstream, card D) — who is connected
              at which judge seat, by ring, next to the expected staffing.
              Same independence as RingStateSection above. */}
          <ScribePresenceSection showId={activeShowId} ringState={ringState} />
        </Stack>
      )}
    </Box>
  );
}
